import { noop } from 'lodash';
import { memo, type ReactElement } from 'react';
import { Collapse } from 'react-collapse';

import { itemBoxStyle } from './Accordion.styles';
import { AccordionHeader } from './AccordionHeader';
import { AccordionItem } from './AccordionItem';

type AccordionItemProps = {
  id: string;
  title: string;
  icon?: ReactElement;
  withDot?: boolean;
};

export type AccordionProps = {
  /** Title of the group of items. */
  title: string;
  /** Subtitle of the group of items. */
  subtitle?: string;
  /** List of items to display. */
  items?: AccordionItemProps[];
  /** Icon displayed on the left of the header. */
  icon?: ReactElement;
  /** Define if the list of item is currently displayed. */
  isOpen?: boolean;
  /** Id of currently selected item. */
  onClickHeader?: () => void;
  /** Callback triggered on the click of the header. */
  selectedItem?: string;
  /** Callback triggered on the click of the item. */
  onClickItem?: (id: string, item: AccordionItemProps) => void;
};

const DEFAULT_ITEMS = [];

export const AccordionBase = ({
  title,
  subtitle = undefined,
  items = DEFAULT_ITEMS,
  icon = undefined,
  isOpen = false,
  onClickHeader = noop,
  selectedItem = undefined,
  onClickItem = noop,
}: AccordionProps) => {
  const withDotOnHeader = items.some((item) => item.withDot);

  return (
    <div css={itemBoxStyle}>
      <AccordionHeader
        title={title}
        subtitle={subtitle}
        icon={icon}
        isOpen={isOpen}
        onClick={onClickHeader}
        withDot={withDotOnHeader}
      />
      <Collapse isOpened={isOpen}>
        {items.map((item) => (
          <AccordionItem
            key={item.id}
            title={item.title}
            icon={item.icon}
            isSelected={selectedItem === item.id}
            onClick={() => onClickItem(item.id, item)}
            withDot={item.withDot}
          />
        ))}
      </Collapse>
    </div>
  );
};

AccordionBase.displayName = 'Accordion';

export const Accordion = memo(AccordionBase);
